import { useEffect } from 'react'

import { toApiError, useAllAccountRelationships, useAllAccounts, useAllUsers, useUser } from '@/api'
import { useAppDispatch, useAppSelector } from '@/store/hooks'

import { directoryLoaded } from '../store'
import { toDirectory } from '../mappers'

/**
 * Loads the accounts, the links between them and the people the signed-in user may see (see `loadDirectory` for who gets whom),
 * and keeps them current: refetched when the window regains focus or the network returns, shared with anything else that asks.
 * The answers are mirrored into the directory in Redux, which the permission rules and screens read.
 *
 * It is a chain of dependent queries: who is asking, then their account, then the right slice of users.
 */
export function useDirectory(enabled: boolean) {
  const dispatch = useAppDispatch()
  const userId = useAppSelector((state) => state.auth.session?.userId)

  const me = useUser(userId, { enabled })
  const accounts = useAllAccounts(undefined, { enabled })
  const relationships = useAllAccountRelationships(undefined, { enabled })
  const mine = accounts.data?.find((account) => account.id === me.data?.accountId)
  // SupplyLogic sees everyone; any other account is sent only its own account id.
  const users = useAllUsers(mine?.accountType === 'SUPPLY_LOGIC' ? {} : { accountId: me.data?.accountId }, { enabled: enabled && Boolean(me.data && accounts.data) })

  useEffect(() => {
    if (accounts.data && relationships.data && users.data) {
      dispatch(directoryLoaded(toDirectory({ accounts: accounts.data, relationships: relationships.data, users: users.data })))
    }
  }, [dispatch, accounts.data, relationships.data, users.data])

  const queries = [me, accounts, relationships, users]
  const failed = queries.find((query) => query.isError)
  const ready = Boolean(accounts.data && relationships.data && users.data)
  return {
    /** Nothing to show yet: still loading for the first time. */
    isPending: !ready && !failed,
    isError: Boolean(failed) && !ready,
    error: failed?.error ?? null,
    /** A reload is running (the first load, a focus refetch, or the Refresh button). */
    isFetching: queries.some((query) => query.isFetching),
    /** Reload everything. Resolves with whether any part failed, and the first error. */
    refetch: async () => {
      const results = await Promise.all(queries.map((query) => query.refetch()))
      const bad = results.find((result) => result.isError)
      return { isError: Boolean(bad), error: bad?.error ? toApiError(bad.error) : null }
    },
  }
}
